/* eslint-disable no-param-reassign */
import {
  all,
  call,
  cancelled,
  put,
  race,
  select,
  take,
  takeEvery,
} from 'redux-saga/effects';
import { eventChannel } from 'redux-saga';
import { navigate } from '@reach/router';
import Keycloak from 'keycloak-js';

import {
  setOrganizationId,
  setInvitedUserOrganizationId,
} from '../../effects/organization';
import { setToken } from '../../effects/application';
import effects from '../../effects';
import {
  login,
  logout,
  register,
  resetPassword,
  setUpOTP,
  updateToken,
} from '../../actions/user';
import { restart } from '../../actions/application';
import getOrganizationDetails from '../organization/getOrganizationDetails';

const refreshInterval = 15 * 60 * 1000;

function* externalListener(channel, adapter) {
  while (true) {
    const event = yield take(channel);
    switch (event.type) {
      case 'OnTokenExpired':
        break;
      case 'OnAuthLogout':
        window.location.href = adapter.createLoginUrl();
        break;
      case 'OnAuthSuccess':
        break;
      case 'OnAuthRefreshError':
        window.location.href = adapter.createLoginUrl();
        break;
      case 'OnAuthRefreshSuccess':
        yield put(setToken({ token: adapter.token }));
        break;
      default:
        break;
    }
  }
}

function* internalListener(adapter) {
  yield takeEvery(updateToken, onUpdateToken(adapter));
  yield takeEvery(logout, onLogout(adapter));
  yield takeEvery(login, onLogin(adapter));
  yield takeEvery(restart, onRestart(adapter));
  yield takeEvery(register, onRegister(adapter));
  yield takeEvery(resetPassword, onResetPassword(adapter));
  yield takeEvery(setUpOTP, onSetUpOTP(adapter));
}

const onRestart = (adapter) => {
  return function* () {
    yield call([adapter, adapter.login], {
      redirectUri: `${window.location.origin}/dashboard/applications/summary`,
    });
  };
};

const onUpdateToken = (adapter) => {
  return function* () {
    yield call([adapter, adapter.updateToken], -1);
    const {
      token,
      tokenParsed: { organizationId },
    } = adapter;
    yield put(setToken({ token }));

    if (window) window.sessionStorage.setItem('token', token);
    yield put(
      setOrganizationId({
        uuid: organizationId,
      }),
    );
    yield call(getOrganizationDetails);
  };
};

const onLogout = (adapter) => {
  return function* () {
    // Hack: redirectUri does not seem to be working properly.
    // navigate to home page before logout.
    yield call(navigate, '/');
    yield call([adapter, adapter.clearToken]);
    yield call([adapter, adapter.logout], {
      redirectUri: `${window.location.origin}/`,
    });
  };
};

const onLogin = (adapter) => {
  return function* () {
    const mostRecentApp = localStorage.getItem('mostRecentApp');
    let redirect;
    if (mostRecentApp === 'CM') {
      redirect = window.location.href;
    } else {
      redirect = yield select(
        (state) => state.application.env.streamingDashboardUrl,
      );
    }
    yield call([adapter, adapter.login], {
      redirectUri: redirect,
    });
  };
};

const onRegister = (adapter) => {
  const urlParams = new URLSearchParams(window.location.search);
  const planId = urlParams.get('planId');
  return function* () {
    window.location.href = 'https://dolby.io/get-started/';
  };
};

const onResetPassword = (adapter) => {
  return function* () {
    adapter.login({ action: 'UPDATE_PASSWORD' });
    /*
    const loginUrl = adapter.createLoginUrl()
    const resetPasswordUrl = loginUrl.replace("auth?", "forgot-credentials?")
    yield call(navigate, resetPasswordUrl) */
  };
};

const onSetUpOTP = (adapter) => {
  return function* () {
    adapter.login({ action: 'CONFIGURE_TOTP' });
  };
};

function* subscribe(adapter) {
  return eventChannel((emit) => {
    adapter.onTokenExpired = () => {
      emit({ type: 'OnTokenExpired' });
    };
    adapter.onAuthError = () => emit({ type: 'OnAuthError' });
    adapter.onAuthSuccess = () => {
      emit({ type: 'OnAuthSuccess' });
      setInterval(() => {
        adapter.updateToken(5);
      }, refreshInterval);
    };
    adapter.onAuthRefreshSuccess = () => {
      emit({ type: 'OnAuthRefreshSuccess' });
    };
    adapter.onAuthRefreshError = () => {
      emit({ type: 'OnAuthRefreshError' });
    };
    adapter.onAuthLogout = () => {
      emit({ type: 'OnAuthLogout' });
    };

    return () => adapter.logout();
  });
}

function* createAdapter() {
  const { keycloakUrl, keycloakRealm, keycloakClientId } = yield select(
    (state) => state.application.env,
  );

  return new Keycloak({
    url: keycloakUrl,
    realm: keycloakRealm,
    clientId: keycloakClientId,
  });
}

function* authenticate(adapter) {
  const isAuthenticated = yield call([adapter, adapter.init], {
    promiseType: 'native',
    onLoad: 'check-sso',
    silentCheckSsoRedirectUri: `${window.location.origin}/silent-check-sso.html`,
  });

  const parameters = new URLSearchParams(window.location.search);
  const requireAuthentication = parameters.get('requireAuthentication');

  if (!isAuthenticated && requireAuthentication === 'true') {
    yield call([adapter, adapter.login], {
      redirectUri: window.location.origin,
    });
  }

  if (isAuthenticated) {
    /* eslint-disable camelcase */
    const {
      token,
      subject: userId,
      realmAccess,
      tokenParsed: {
        given_name: firstName,
        family_name: lastName,
        email,
        phone_number: phoneNumber,
        organizationId,
        signup_pricing_streaming_plan_id: signUpStreamingPlanId,
      },
    } = adapter;
    yield put(setToken({ token }));

    if (window) window.sessionStorage.setItem('token', token);
    const userMetadata = {
      id: userId,
      firstName,
      lastName,
      email,
      phoneNumber,
      signUpStreamingPlanId,
    };
    yield put(effects.user.login(userMetadata));

    // if the user is onboarding via an invitation, set the Org ID without downstream effects
    if (organizationId) {
      if (realmAccess) {
        yield put(
          setOrganizationId({
            uuid: organizationId,
          }),
        );
      } else {
        yield put(
          setInvitedUserOrganizationId({
            uuid: organizationId,
          }),
        );
      }
    } else {
      yield call(navigate, '/enrollment/');
    }
  } else {
    yield put(setToken({ token: false }));
  }
}

export default function* watchAuthentication() {
  if (typeof new Keycloak() === 'object') {
    // This logs errors when doing Gatsby build because Keycloak is not loaded
    const adapter = yield call(createAdapter);
    const channel = yield call(subscribe, adapter);
    try {
      yield call(authenticate, adapter);
    } catch {
      yield put(setToken({ token: false }));
    }

    try {
      const { cancel } = yield race({
        status: all([
          call(externalListener, channel, adapter),
          call(internalListener, adapter),
        ]),
        cancel: take(logout),
      });

      if (cancel) {
        channel.close();
      }
    } finally {
      if (yield cancelled()) channel.close();
    }
  }
}
